/* eslint-disable no-param-reassign */
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { Node as ProseMirrorNode } from 'prosemirror-model';

import { DEFAULT_COL_WIDTH, MIN_COL_WIDTH } from '../utils/tableConstants';

type ConstraintState = {
  lastPluginWidths: number[] | null;
  lastChangedIndex: number;
  lastClampedWidths: number[] | null;
};

const OBSERVER_CONFIG: MutationObserverInit = {
  attributes: true,
  attributeFilter: ['style'],
  subtree: true,
};

function applyWidths(
  table: HTMLTableElement,
  cols: HTMLElement[],
  newWidths: number[],
  containerWidth: number
) {
  table.style.width = `${containerWidth}px`;
  table.style.minWidth = '0';

  cols.forEach((col, i) => {
    col.style.width = `${newWidths[i]}px`;
  });
}

function scaleProportionally(widths: number[], containerWidth: number) {
  const total = widths.reduce((sum, w) => sum + w, 0);
  const scale = containerWidth / total;
  const result = widths.map((w) =>
    Math.max(MIN_COL_WIDTH, Math.round(w * scale))
  );

  let diff = result.reduce((sum, w) => sum + w, 0) - containerWidth;
  const sorted = result
    .map((w, i) => ({ w, i }))
    .filter((x) => x.w > MIN_COL_WIDTH)
    .sort((a, b) => b.w - a.w);

  sorted.forEach((x) => {
    if (diff <= 0) return;
    const reduction = Math.min(diff, x.w - MIN_COL_WIDTH);
    result[x.i] -= reduction;
    diff -= reduction;
  });

  return result;
}

function enforceTableConstraints(
  tableWrapper: HTMLElement,
  state: ConstraintState,
  observer: MutationObserver
) {
  const table = tableWrapper.querySelector('table');
  if (!table) return;

  const cols = Array.from(table.querySelectorAll<HTMLElement>('colgroup col'));
  if (cols.length < 2) return;

  const hasPixelWidths = cols.some((col) => {
    const w = col.style.width;
    return w && !w.includes('%') && parseInt(w, 10) > 0;
  });
  if (!hasPixelWidths) return;

  const wrapperStyles = getComputedStyle(tableWrapper);
  const containerWidth =
    tableWrapper.clientWidth -
    parseFloat(wrapperStyles.paddingLeft) -
    parseFloat(wrapperStyles.paddingRight);
  if (containerWidth <= 0) return;

  const pluginWidths = cols.map((col) => {
    const w = parseInt(col.style.width, 10);
    return w > 0 ? w : DEFAULT_COL_WIDTH;
  });
  const total = pluginWidths.reduce((sum, w) => sum + w, 0);

  if (total <= containerWidth) {
    state.lastPluginWidths = pluginWidths.slice();
    state.lastChangedIndex = -1;
    state.lastClampedWidths = null;
    return;
  }

  let changedIndex = -1;
  let changeDelta = 0;
  const prevWidths = state.lastPluginWidths;
  if (prevWidths && prevWidths.length === pluginWidths.length) {
    let maxChange = 0;
    pluginWidths.forEach((w, i) => {
      const change = Math.abs(w - prevWidths[i]);
      if (change > maxChange) {
        maxChange = change;
        changedIndex = i;
        changeDelta = w - prevWidths[i];
      }
    });
  }

  if (changedIndex < 0 && state.lastChangedIndex >= 0) {
    changedIndex = state.lastChangedIndex;
  }
  if (changedIndex >= 0) {
    state.lastChangedIndex = changedIndex;
  }

  state.lastPluginWidths = pluginWidths.slice();

  let newWidths;

  if (
    changedIndex >= 0 &&
    state.lastClampedWidths &&
    state.lastClampedWidths.length === pluginWidths.length
  ) {
    newWidths = state.lastClampedWidths.slice();
    newWidths[changedIndex] = Math.max(
      MIN_COL_WIDTH,
      newWidths[changedIndex] + changeDelta
    );

    const newTotal = newWidths.reduce((sum, w) => sum + w, 0);
    let remaining = newTotal - containerWidth;

    if (remaining > 0) {
      for (let i = changedIndex + 1; i < newWidths.length; i += 1) {
        if (remaining <= 0) break;
        const shrink = Math.min(remaining, newWidths[i] - MIN_COL_WIDTH);
        newWidths[i] -= shrink;
        remaining -= shrink;
      }

      for (let i = changedIndex - 1; i >= 0; i -= 1) {
        if (remaining <= 0) break;
        const shrink = Math.min(remaining, newWidths[i] - MIN_COL_WIDTH);
        newWidths[i] -= shrink;
        remaining -= shrink;
      }

      if (remaining > 0) {
        newWidths[changedIndex] -= remaining;
      }
    } else if (remaining < 0) {
      let spare = -remaining;

      for (let i = changedIndex + 1; i < newWidths.length; i += 1) {
        if (spare <= 0) break;
        const maxRestore = pluginWidths[i] - newWidths[i];
        if (maxRestore > 0) {
          const restore = Math.min(spare, maxRestore);
          newWidths[i] += restore;
          spare -= restore;
        }
      }

      for (let i = changedIndex - 1; i >= 0; i -= 1) {
        if (spare <= 0) break;
        const maxRestore = pluginWidths[i] - newWidths[i];
        if (maxRestore > 0) {
          const restore = Math.min(spare, maxRestore);
          newWidths[i] += restore;
          spare -= restore;
        }
      }
    }
  } else {
    newWidths = scaleProportionally(pluginWidths, containerWidth);
  }

  state.lastClampedWidths = newWidths.slice();

  // Disconnect before applying widths so we don't react to our own mutations
  observer.disconnect();
  applyWidths(table, cols, newWidths, containerWidth);
  observer.observe(tableWrapper, OBSERVER_CONFIG);
}

function hasColwidth(cell: ProseMirrorNode): boolean {
  const cw = cell.attrs.colwidth;
  return Array.isArray(cw) && cw.some((w: number) => w > 0);
}

function fillMissingColwidths(editorView: EditorView) {
  const { state } = editorView;
  let { tr } = state;
  let modified = false;

  state.doc.descendants((node, pos) => {
    if (node.type.name !== 'table') return true;

    const firstRow = node.child(0);
    if (!firstRow || firstRow.childCount < 2) return false;

    let hasAny = false;
    let hasAll = true;
    firstRow.forEach((cell) => {
      if (hasColwidth(cell)) hasAny = true;
      else hasAll = false;
    });

    if (!hasAny || hasAll) return false;

    const tableDOM = editorView.nodeDOM(pos);
    if (!tableDOM) return false;
    const tableEl =
      tableDOM instanceof HTMLTableElement
        ? tableDOM
        : (tableDOM as HTMLElement).querySelector?.('table');
    if (!tableEl) return false;

    const firstRowEl = tableEl.querySelector('tr');
    if (!firstRowEl) return false;
    const cellEls = firstRowEl.querySelectorAll('td, th');

    let cellPos = pos + 2;
    firstRow.forEach((cell, _offset, index) => {
      if (!hasColwidth(cell) && cellEls[index]) {
        const computedWidth = Math.round(
          cellEls[index].getBoundingClientRect().width
        );
        if (computedWidth > 0) {
          tr = tr.setNodeMarkup(cellPos, null, {
            ...cell.attrs,
            colwidth: [computedWidth],
          });
          modified = true;
        }
      }
      cellPos += cell.nodeSize;
    });

    return false;
  });

  if (modified) {
    editorView.dispatch(tr);
  }
}

export const TableColumnConstraints = Extension.create({
  name: 'tableColumnConstraints',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('tableColumnConstraints'),
        view(editorView) {
          const wrapperStates = new WeakMap<HTMLElement, ConstraintState>();
          let observers: MutationObserver[] = [];
          let trackedWrappers: HTMLElement[] = [];

          const sync = () => {
            const newWrappers = Array.from(
              editorView.dom.querySelectorAll<HTMLElement>('.tableWrapper')
            );

            const changed =
              newWrappers.length !== trackedWrappers.length ||
              newWrappers.some((w, i) => w !== trackedWrappers[i]);

            if (!changed) return;

            observers.forEach((obs) => obs.disconnect());
            observers = [];
            trackedWrappers = newWrappers;

            newWrappers.forEach((wrapper) => {
              if (!wrapperStates.has(wrapper)) {
                wrapperStates.set(wrapper, {
                  lastPluginWidths: null,
                  lastChangedIndex: -1,
                  lastClampedWidths: null,
                });
              }
              const state = wrapperStates.get(wrapper)!;

              const observer = new MutationObserver(() => {
                enforceTableConstraints(wrapper, state, observer);
              });
              observer.observe(wrapper, OBSERVER_CONFIG);
              observers.push(observer);

              enforceTableConstraints(wrapper, state, observer);
            });
          };

          sync();

          return {
            update() {
              sync();
              fillMissingColwidths(editorView);
            },
            destroy() {
              observers.forEach((obs) => obs.disconnect());
            },
          };
        },
      }),
    ];
  },
});
